import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  DownOutlined,
  LoadingOutlined,
  RobotOutlined,
  SendOutlined,
  StopOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { useChat } from "@ai-sdk/react";
import {
  DefaultChatTransport,
  getToolName,
  isToolUIPart,
  type DynamicToolUIPart,
  type ToolUIPart,
  type UIMessage,
} from "ai";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Typography } from "antd";
import type React from "react";
import { Streamdown } from "streamdown";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { traineeMealsPlanActions } from "@fitness-app/app-store";
import type { AgentChatMessage, AgentChatSession } from "@fitness-app/data-models";
import {
  Conversation,
  ConversationContent,
  ConversationEmptyState,
  ConversationScrollButton,
} from "@fitness-app/web-ui/components/ai-elements/conversation";
import { Loader } from "@fitness-app/web-ui/components/ai-elements/loader";
import { Message, MessageContent, MessageResponse } from "@fitness-app/web-ui/components/ai-elements/message";
import {
  PromptInput,
  PromptInputBody,
  PromptInputFooter,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputTools,
} from "@fitness-app/web-ui/components/ai-elements/prompt-input";
import { Reasoning, ReasoningContent, ReasoningTrigger } from "@fitness-app/web-ui/components/ai-elements/reasoning";

import { getClientWebApiBaseUrl } from "~/modules/Sales/SalesAgent/constants";
import { mapStoredMessage } from "~/modules/Sales/SalesAgent/messageUtils";
import { useTraineeSearchParams } from "~/modules/Trainee/TraineeProfile/scope";
import { supabase, useAppDispatch } from "~/store/initializeStore";
import { Logger } from "~/utils/Logger";

import { ProposedDishCard, type ProposedDishSummary } from "./ProposedDishCard";

type NutritionToolPart = ToolUIPart | DynamicToolUIPart;

const nutritionPlanAgentChatType = "nutrition_plan_agent";
const nutritionPlanAgentSessionsQueryKey = ["nutritionPlanAgentChatSessions"];
const nutritionPlanAgentMessagesQueryKey = ["nutritionPlanAgentChatMessages"];

const normalizePlanId = (value: string | null | undefined) => (value && value !== "empty" ? value : null);

const toolLabels: Record<string, string> = {
  "get-nutrition-plan-schedule": "Pobranie grafiku",
  getNutritionPlanScheduleTool: "Pobranie grafiku",
  "search-nutrition-plan-dishes": "Wyszukiwanie potraw",
  searchNutritionPlanDishesTool: "Wyszukiwanie potraw",
  "search-nutrition-plan-ingredients": "Wyszukiwanie produktów",
  searchNutritionPlanIngredientsTool: "Wyszukiwanie produktów",
  "add-item-to-nutrition-plan-meal": "Dodanie do posiłku",
  addItemToNutritionPlanMealTool: "Dodanie do posiłku",
  "replace-nutrition-plan-item": "Podmiana w planie",
  replaceNutritionPlanItemTool: "Podmiana w planie",
  "move-dish-in-nutrition-plan": "Przeniesienie potrawy",
  moveDishInNutritionPlanTool: "Przeniesienie potrawy",
  "remove-dish-from-nutrition-plan": "Usunięcie potrawy",
  removeDishFromNutritionPlanTool: "Usunięcie potrawy",
  "reorder-meal-dishes": "Zmiana kolejności",
  reorderMealDishesTool: "Zmiana kolejności",
  "update-nutrition-plan-dish-details": "Edycja potrawy",
  updateNutritionPlanDishDetailsTool: "Edycja potrawy",
  "update-nutrition-plan-standalone-ingredient-portion": "Zmiana porcji produktu",
  updateNutritionPlanStandaloneIngredientPortionTool: "Zmiana porcji produktu",
  "update-nutrition-plan-dish-ingredient": "Zmiana składnika potrawy",
  updateNutritionPlanDishIngredientTool: "Zmiana składnika potrawy",
  "remove-ingredient-from-nutrition-plan-dish": "Usunięcie składnika",
  removeIngredientFromNutritionPlanDishTool: "Usunięcie składnika",
  "reorder-nutrition-plan-dish-ingredients": "Zmiana kolejności składników",
  reorderNutritionPlanDishIngredientsTool: "Zmiana kolejności składników",
  "add-ingredient-to-nutrition-plan-dish": "Dodanie składnika",
  addIngredientToNutritionPlanDishTool: "Dodanie składnika",
  "propose-dish": "Propozycja potrawy",
  proposeDishTool: "Propozycja potrawy",
  "adjust-nutrition-plan-dish": "Dostosowanie kaloryki potrawy",
  adjustNutritionPlanDishTool: "Dostosowanie kaloryki potrawy",
};

const PROPOSE_DISH_TOOL_NAMES = new Set(["propose-dish", "proposeDishTool"]);

const getProposedDishSummary = (part: NutritionToolPart): ProposedDishSummary | null => {
  if (!PROPOSE_DISH_TOOL_NAMES.has(getToolName(part))) return null;
  if (part.state !== "output-available") return null;

  const output = part.output;
  if (!output || typeof output !== "object") return null;

  const dish = (output as { dish?: unknown }).dish;
  if (!dish || typeof dish !== "object" || !("name" in dish) || !("macros" in dish)) return null;

  return dish as ProposedDishSummary;
};

const toolStateLabels: Record<NutritionToolPart["state"], string> = {
  "approval-requested": "Oczekuje",
  "approval-responded": "Potwierdzono",
  "input-available": "Uruchomione",
  "input-streaming": "Przygotowanie",
  "output-available": "Zakończone",
  "output-denied": "Odrzucone",
  "output-error": "Błąd",
};

const toolStateClasses: Record<NutritionToolPart["state"], string> = {
  "approval-requested": "border-amber-200 bg-amber-50 text-amber-700",
  "approval-responded": "border-blue-200 bg-blue-50 text-blue-700",
  "input-available": "border-blue-200 bg-blue-50 text-blue-700",
  "input-streaming": "border-gray-200 bg-gray-50 text-gray-600",
  "output-available": "border-emerald-200 bg-emerald-50 text-emerald-700",
  "output-denied": "border-gray-200 bg-gray-50 text-gray-600",
  "output-error": "border-red-200 bg-red-50 text-red-700",
};

const toolStateIcon = (state: NutritionToolPart["state"]) => {
  if (state === "output-available") return <CheckCircleOutlined />;
  if (state === "output-error") return <CloseCircleOutlined />;
  if (state === "input-available" || state === "input-streaming") return <LoadingOutlined />;
  return <ToolOutlined />;
};

const formatToolName = (part: NutritionToolPart) => {
  const toolName = getToolName(part);
  return (
    toolLabels[toolName] ??
    toolName
      .replace(/^tool-/, "")
      .replace(/Tool$/, "")
      .replaceAll("-", " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .toLowerCase()
  );
};

const stringifyToolValue = (value: unknown) => {
  if (value === undefined) return "";
  if (typeof value === "string") return value;
  return JSON.stringify(value, null, 2);
};

const getRecordValue = (value: unknown, key: string) => {
  if (!value || typeof value !== "object" || !(key in value)) return undefined;
  return (value as Record<string, unknown>)[key];
};

const getNamedRecordSummary = (value: unknown, prefix: string) => {
  if (!value || typeof value !== "object") return undefined;

  const name = getRecordValue(value, "name");
  if (typeof name === "string" && name.trim()) return `${prefix}: ${name}`;

  const id = getRecordValue(value, "id");
  if (typeof id === "string" && id.trim()) return `${prefix}: ${id}`;

  return undefined;
};

const getToolOutputSummary = (part: NutritionToolPart) => {
  if (part.state === "output-error") return part.errorText;
  if (part.state !== "output-available") return "Narzędzie pracuje na aktualnym planie.";

  const output = part.output;
  if (!output || typeof output !== "object") return "Wynik zapisany.";
  const removedDishName = getRecordValue(output, "removedDishName");
  if (typeof removedDishName === "string") return `Usunięto: ${removedDishName}`;
  const movedDishName = getRecordValue(output, "movedDishName");
  if (typeof movedDishName === "string") return `Przeniesiono: ${movedDishName}`;
  const removedIngredient = getNamedRecordSummary(getRecordValue(output, "removedIngredient"), "Usunięto składnik");
  if (removedIngredient) return removedIngredient;
  const addedIngredient = getNamedRecordSummary(getRecordValue(output, "addedIngredient"), "Dodano składnik");
  if (addedIngredient) return addedIngredient;
  const updatedIngredient = getNamedRecordSummary(getRecordValue(output, "updatedIngredient"), "Zmieniono składnik");
  if (updatedIngredient) return updatedIngredient;
  const updatedDish = getNamedRecordSummary(getRecordValue(output, "updatedDish"), "Zmieniono potrawę");
  if (updatedDish) return updatedDish;
  const adjustedDish = getNamedRecordSummary(getRecordValue(output, "adjustedDish"), "Dostosowano");
  if (adjustedDish) return adjustedDish;
  if ("schedule" in output) return "Pobrano aktualny grafik planu.";

  return "Narzędzie zakończyło pracę.";
};

const NutritionToolCall = ({ part }: { part: NutritionToolPart }) => {
  const [isOpen, setIsOpen] = useState(part.state === "output-error");
  const input = stringifyToolValue(part.input);
  const output =
    part.state === "output-available"
      ? stringifyToolValue(part.output)
      : part.state === "output-error"
        ? part.errorText
        : "";

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white text-xs shadow-sm">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-gray-50"
        onClick={() => setIsOpen((value) => !value)}
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-600">
          <ToolOutlined />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium text-gray-900">{formatToolName(part)}</span>
          <span className="block truncate text-gray-500">{getToolOutputSummary(part)}</span>
        </span>
        <span
          className={[
            "inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 font-medium",
            toolStateClasses[part.state],
          ].join(" ")}
        >
          {toolStateIcon(part.state)}
          {toolStateLabels[part.state]}
        </span>
        <DownOutlined
          className={["text-[10px] text-gray-400 transition-transform", isOpen ? "rotate-180" : ""].join(" ")}
        />
      </button>

      {isOpen ? (
        <div className="space-y-2 border-t border-gray-100 bg-gray-50 px-3 py-3">
          {input ? (
            <div>
              <div className="mb-1 font-medium uppercase tracking-wide text-gray-500">Parametry</div>
              <pre className="max-h-32 overflow-auto rounded-md border border-gray-200 bg-white p-2 font-mono text-[11px] leading-4 text-gray-700">
                {input}
              </pre>
            </div>
          ) : null}
          {output ? (
            <div>
              <div className="mb-1 font-medium uppercase tracking-wide text-gray-500">
                {part.state === "output-error" ? "Błąd" : "Wynik"}
              </div>
              <pre className="max-h-40 overflow-auto rounded-md border border-gray-200 bg-white p-2 font-mono text-[11px] leading-4 text-gray-700">
                {output}
              </pre>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const NutritionMessageParts = ({
  isLastMessage,
  isStreaming,
  message,
  onPlanRefresh,
  planId,
  traineeId,
}: {
  isLastMessage: boolean;
  isStreaming: boolean;
  message: UIMessage;
  onPlanRefresh: () => void;
  planId: string;
  traineeId: string;
}) => {
  const parts = message.parts ?? [];
  const reasoningParts = parts.filter((part) => part.type === "reasoning");
  const reasoningText = reasoningParts
    .map((part) => part.text)
    .join("\n\n")
    .trim();
  const lastPart = parts.at(-1);
  const isReasoningStreaming = isLastMessage && isStreaming && lastPart?.type === "reasoning";
  const hasVisibleContent =
    Boolean(reasoningText) ||
    parts.some((part) => {
      if (part.type === "text") return part.text.trim();
      return isToolUIPart(part);
    });

  return (
    <div className="space-y-2">
      {reasoningText ? (
        <Reasoning className="w-full" isStreaming={isReasoningStreaming}>
          <ReasoningTrigger />
          <ReasoningContent>{reasoningText}</ReasoningContent>
        </Reasoning>
      ) : null}
      {parts.map((part, index) => {
        if (part.type === "text") {
          return (
            <MessageResponse key={`${message.id}-text-${index}`}>
              <Streamdown
                controls={false}
                mode={isLastMessage && isStreaming ? "streaming" : "static"}
                parseIncompleteMarkdown
              >
                {part.text}
              </Streamdown>
            </MessageResponse>
          );
        }

        if (!isToolUIPart(part)) return null;

        const proposedDish = getProposedDishSummary(part);

        if (proposedDish) {
          return (
            <ProposedDishCard
              key={`${message.id}-${part.toolCallId}`}
              dish={proposedDish}
              dishSpec={part.input}
              planId={planId}
              traineeId={traineeId}
              onAdded={onPlanRefresh}
            />
          );
        }

        return <NutritionToolCall key={`${message.id}-${part.toolCallId}`} part={part} />;
      })}
      {isLastMessage && isStreaming && !hasVisibleContent ? <Loader /> : null}
    </div>
  );
};

const NutritionPlanAgentPrompt = memo(
  ({
    isBusy,
    onStop,
    onSubmit,
    status,
  }: {
    isBusy: boolean;
    onStop: () => void;
    onSubmit: (value: string) => void;
    status: "ready" | "submitted" | "streaming" | "error";
  }) => {
    const [input, setInput] = useState("");

    const handleSubmit = useCallback(
      (value: string) => {
        onSubmit(value);
        setInput("");
      },
      [onSubmit],
    );

    const handlePromptKeyDown = useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;

      event.preventDefault();
      event.currentTarget.form?.requestSubmit();
    }, []);

    return (
      <PromptInput
        className="rounded-lg border-gray-200 bg-white shadow-none"
        onSubmit={(payload) => handleSubmit(payload.text)}
      >
        <PromptInputBody className="px-4 pt-3">
          <PromptInputTextarea
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={handlePromptKeyDown}
            disabled={isBusy}
            rows={3}
            placeholder="Opisz zmianę w planie..."
          />
        </PromptInputBody>
        <PromptInputFooter className="px-3 pb-3 pt-2">
          <PromptInputTools />
          {isBusy ? (
            <button
              type="button"
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#a28f70] bg-white text-[#7d6b4f] transition-colors hover:bg-[#f8f4ee] hover:text-[#4f402d]"
              onClick={onStop}
              aria-label="Zatrzymaj odpowiedź"
            >
              <StopOutlined className="text-base" />
            </button>
          ) : (
            <PromptInputSubmit status={status} disabled={!input.trim()}>
              <SendOutlined />
              <span className="sr-only">Wyślij</span>
            </PromptInputSubmit>
          )}
        </PromptInputFooter>
      </PromptInput>
    );
  },
);
NutritionPlanAgentPrompt.displayName = "NutritionPlanAgentPrompt";

export const NutritionPlanAgentChat = ({
  planId,
  traineeId,
  onClose,
}: {
  planId: string;
  traineeId: string;
  onClose?: () => void;
}): React.ReactElement => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const [searchParams] = useTraineeSearchParams();
  const urlPlanId = normalizePlanId(searchParams.get("planId"));
  const effectivePlanId = urlPlanId || planId;
  // Transport i callbacki czatu żyją dłużej niż render, a plan może się zmienić w trakcie
  // rozmowy. Czytamy go z parametrów tej karty, nie z `window.location`, który należy do
  // widocznej karty.
  const effectivePlanIdRef = useRef(effectivePlanId);
  effectivePlanIdRef.current = effectivePlanId;
  const chatScope = `${effectivePlanId}:${traineeId}`;
  const [activeSession, setActiveSession] = useState<{ id: string | null; scope: string }>({
    id: null,
    scope: "",
  });
  const activeSessionId = activeSession.scope === chatScope ? activeSession.id : null;
  const activeSessionIdRef = useRef<string | null>(null);
  const previousChatScopeRef = useRef<string | null>(null);
  const skipHydrationSessionIdRef = useRef<string | null>(null);
  const skipHydrationTokenRef = useRef(0);

  useEffect(() => {
    if (urlPlanId && urlPlanId !== planId) {
      Logger.error(
        {
          propPlanId: planId,
          urlPlanId,
        },
        "[nutrition-plan-chat] planId mismatch, using URL planId",
      );
    }
  }, [planId, urlPlanId]);

  useEffect(() => {
    activeSessionIdRef.current = activeSessionId;
  }, [activeSessionId]);

  const findSession = useCallback(async () => {
    const { data, error } = await supabase
      .from("agent_chat_session")
      .select("*")
      .eq("agentType", nutritionPlanAgentChatType)
      .eq("contextId", effectivePlanId)
      .maybeSingle();

    if (error) throw new Error(error.message);
    return data ? (data as AgentChatSession) : null;
  }, [effectivePlanId]);

  const { data: chatSession, isLoading: isSessionLoading } = useQuery({
    queryKey: [...nutritionPlanAgentSessionsQueryKey, effectivePlanId, planId, traineeId],
    enabled: Boolean(effectivePlanId && traineeId),
    retry: false,
    queryFn: findSession,
  });

  const transport = useMemo(
    () =>
      new DefaultChatTransport<UIMessage>({
        api: `${getClientWebApiBaseUrl()}/api/trainers/ai/nutrition-plan-chat`,
        fetch: async (input, init) => {
          const response = await fetch(input, init);
          const responseSessionId = response.headers.get("X-Agent-Chat-Session-Id");

          if (responseSessionId) {
            activeSessionIdRef.current = responseSessionId;
            skipHydrationSessionIdRef.current = responseSessionId;
            setActiveSession({ id: responseSessionId, scope: chatScope });
            void queryClient.invalidateQueries({ queryKey: nutritionPlanAgentSessionsQueryKey });
          }

          return response;
        },
        prepareSendMessagesRequest: async ({ api, body, headers, credentials, id, messages, trigger, messageId }) => {
          const token = (await supabase.auth.getSession()).data.session?.access_token;
          const latestMessage = messages.at(-1);
          const requestPlanId = effectivePlanIdRef.current;

          return {
            api,
            body: {
              ...body,
              id,
              planId: requestPlanId,
              traineeId,
              sessionId: activeSessionIdRef.current ?? undefined,
              messages: latestMessage ? [latestMessage] : [],
              trigger,
              messageId,
            },
            credentials,
            headers: {
              ...headers,
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          };
        },
      }),
    [chatScope, queryClient, traineeId],
  );

  const refreshPlan = useCallback(async () => {
    const refreshPlanId = effectivePlanIdRef.current;

    try {
      await dispatch(traineeMealsPlanActions.fetchMealsPlanWithDetails({ id: refreshPlanId })).unwrap();
    } catch (fetchError) {
      Logger.error(fetchError, `[nutrition-plan-chat] failed to refresh nutrition plan ${refreshPlanId}`);
    }
  }, [dispatch]);

  const { messages, sendMessage, setMessages, status, stop, error, clearError } = useChat<UIMessage>({
    transport,
    experimental_throttle: 50,
    onFinish: async () => {
      const sessionId = activeSessionIdRef.current;
      const skipHydrationToken = skipHydrationTokenRef.current;

      await Promise.all([
        refreshPlan(),
        queryClient.invalidateQueries({ queryKey: nutritionPlanAgentSessionsQueryKey }),
        sessionId
          ? queryClient.invalidateQueries({ queryKey: [...nutritionPlanAgentMessagesQueryKey, sessionId] })
          : Promise.resolve(),
      ]);

      if (skipHydrationTokenRef.current === skipHydrationToken) {
        skipHydrationSessionIdRef.current = null;
      }
    },
  });

  const isBusy = status === "submitted" || status === "streaming";

  const { data: storedMessages, isLoading: areMessagesLoading } = useQuery({
    queryKey: [...nutritionPlanAgentMessagesQueryKey, activeSessionId],
    enabled: Boolean(activeSessionId),
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("agent_chat_message")
        .select("*")
        .eq("sessionId", activeSessionId!)
        .order("createdAt", { ascending: true });

      if (error) throw new Error(error.message);
      return (data ?? []) as AgentChatMessage[];
    },
  });
  const shouldShowMessagesLoader = Boolean(
    (isSessionLoading && !activeSessionId) ||
    (areMessagesLoading && activeSessionId && skipHydrationSessionIdRef.current !== activeSessionId),
  );

  useEffect(() => {
    if (previousChatScopeRef.current === chatScope) return;

    previousChatScopeRef.current = chatScope;
    activeSessionIdRef.current = null;
    skipHydrationTokenRef.current += 1;
    skipHydrationSessionIdRef.current = null;
    setMessages([]);
  }, [chatScope, setMessages]);

  useEffect(() => {
    if (!chatSession || activeSessionIdRef.current) return;

    activeSessionIdRef.current = chatSession.id;
    setActiveSession({ id: chatSession.id, scope: chatScope });
  }, [chatScope, chatSession]);

  useEffect(() => {
    if (!storedMessages || isBusy) return;
    if (activeSessionId && skipHydrationSessionIdRef.current === activeSessionId) return;

    setMessages(storedMessages.map(mapStoredMessage));
  }, [activeSessionId, isBusy, storedMessages, setMessages]);

  const submit = useCallback(
    (value: string) => {
      const text = value.trim();
      if (!text || isBusy) return;
      const requestPlanId = effectivePlanIdRef.current;

      clearError();
      skipHydrationTokenRef.current += 1;
      skipHydrationSessionIdRef.current = activeSessionIdRef.current;
      void sendMessage(
        {
          id: crypto.randomUUID(),
          role: "user",
          parts: [{ type: "text", text }],
        },
        { body: { sessionId: activeSessionIdRef.current ?? undefined } },
      ).catch((sendError) => Logger.error(sendError, `[nutrition-plan-chat] failed to send message ${requestPlanId}`));
    },
    [clearError, isBusy, sendMessage],
  );

  return (
    <Card
      className="h-fit min-w-[320px] flex-1 overflow-hidden rounded-lg border border-gray-100 shadow-[0_14px_40px_rgba(15,23,42,0.12)] lg:max-w-[390px]"
      styles={{ body: { padding: 0 } }}
    >
      <div className="flex h-[860px] max-h-[calc(100vh-116px)] min-h-0 flex-col">
        <div className="shrink-0 border-b border-gray-100 px-4 py-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <Typography.Text className="text-xs font-medium uppercase text-gray-500">Agent diety</Typography.Text>
              <Typography.Title level={5} className="!mb-0 !mt-1">
                Edycja draftu
              </Typography.Title>
            </div>
            {onClose ? (
              <Button
                aria-label="Schowaj czat"
                className="text-gray-500 hover:bg-gray-50 hover:text-gray-900"
                type="text"
                shape="circle"
                size="small"
                icon={<CloseOutlined />}
                onClick={onClose}
              />
            ) : null}
          </div>
        </div>

        <Conversation>
          <ConversationContent className="px-4 py-4">
            {shouldShowMessagesLoader ? (
              <Message from="assistant">
                <MessageContent className="border-transparent bg-transparent px-0 py-0 text-gray-500 shadow-none">
                  <Loader />
                </MessageContent>
              </Message>
            ) : messages.length ? (
              messages.map((message, index) => (
                <Message key={message.id} from={message.role}>
                  <MessageContent
                    className={
                      message.role === "user"
                        ? "rounded-[18px] border-[#d8cbb9] bg-[#f8f4ee] px-3 py-2 text-gray-950 shadow-none"
                        : "border-transparent bg-transparent px-0 py-0 text-gray-900 shadow-none"
                    }
                  >
                    <NutritionMessageParts
                      message={message}
                      isLastMessage={index === messages.length - 1}
                      isStreaming={status === "streaming"}
                      planId={effectivePlanId}
                      traineeId={traineeId}
                      onPlanRefresh={() => void refreshPlan()}
                    />
                  </MessageContent>
                </Message>
              ))
            ) : (
              <ConversationEmptyState
                icon={<RobotOutlined className="text-2xl" />}
                title="Edycja draftu"
                description="Napisz np. „Przenieś owsiankę z wtorku na śniadanie w piątek” albo „Usuń jajecznicę z poniedziałku”."
              />
            )}
            {status === "submitted" ? (
              <Message from="assistant">
                <MessageContent className="border-transparent bg-transparent px-0 py-0 text-gray-500 shadow-none">
                  <Loader />
                </MessageContent>
              </Message>
            ) : null}
            {error ? <Typography.Text type="danger">{error.message}</Typography.Text> : null}
          </ConversationContent>
          <ConversationScrollButton />
        </Conversation>

        <div className="shrink-0 border-t border-gray-100 bg-white px-4 py-4">
          <NutritionPlanAgentPrompt isBusy={isBusy} onStop={stop} onSubmit={submit} status={status} />
        </div>
      </div>
    </Card>
  );
};
